// Execution Blueprint (EXW) — coach-side endpoints only.
// docs/execution-blueprint/COACH-API.md is the digest; 08-fe-handoff.md §4 is
// the verbatim backend contract. The CEO REST API, socket/Interview layer and
// admin route are out of scope for this repo — never add them here.
//
// Only GET_DASHBOARD is wired to a store action so far (dashboard screen).
// The other 7 are declared now so the contract is in one place, but nothing
// calls them yet - see docs/execution-blueprint/PROGRESS.md "Current state of
// play" before wiring the next one.
export const executionBlueprintUrls = {
  GET_DASHBOARD: "/coach/execution-blueprint/dashboard",
  POST_REVIEW_ENTER: "/coach/execution-blueprint/{uniqueId}/review/enter",
  GET_REVIEW: "/coach/execution-blueprint/{uniqueId}/review",
  SET_ITEM_STATUS:
    "/coach/execution-blueprint/{uniqueId}/review/items/{itemId}/status",
  ADD_COMMENT: "/coach/execution-blueprint/{uniqueId}/review/comments",
  DELETE_COMMENT:
    "/coach/execution-blueprint/{uniqueId}/review/comments/{commentId}",
  GET_BLUEPRINT_MODE: "/coach/execution-blueprint/{uniqueId}/blueprint-mode",
  SUBMIT_REVIEW: "/coach/execution-blueprint/{uniqueId}/review/submit",

  // Live Review, step 1. Added 2026-08-27 from `12-live-review-fe-handoff.md`
  // §4 — the first live-review endpoint to exist, and the reason the localStorage
  // start gate in `ExecutionBlueprintReviewStore` could finally be deleted.
  //
  // POST, no body, `CoachAuthGuard`. 409 unless `blueprint_state` is
  // `COACH_REVIEW_SUBMITTED`. Returns the blueprint's new state plus
  // `live_review_started_at` and `live_review_step`.
  //
  // There is deliberately NO coach-side counterpart to this for *joining*: once
  // started, both parties join over the `/live-review` socket namespace (§5),
  // not over REST. And there is no CEO-side start at all — the CEO's existing
  // `GET :uniqueId` returning `live_review_started_at` is their signal (§2).
  START_LIVE_REVIEW:
    "/coach/execution-blueprint/{uniqueId}/live-review/start",

  // ── Post-lock reads (the Final Locked Blueprint screens) ──────────────────
  // Confirmed by the user 2026-08-31. The three locked screens (hub → Blueprint
  // / WIG Connections / Year 1 tabs) used to read the live socket snapshot;
  // that was removed on instruction, because once the terminal lock fires the
  // session is over and the socket is closed, so a screen you can open days
  // later cannot depend on being in the room.
  //
  // ⚠️ The path segment is `locked`, NOT `year1` — and the handoff now agrees.
  // §9 of `12-live-review-fe-handoff.md` used to name these as
  // `GET :uniqueId/year1/{sections,connectors,shortlist}`; we built against
  // `locked` anyway because that is what shipped, and the 2026-09-03 revision
  // of that doc corrects the names and notes they exist coach-side as well as
  // CEO-side. Nothing here changed; the note is kept so the old naming is
  // recognisable if it turns up in an older copy of the doc.
  //
  // Its companion citation is still wrong, though: §9 points at
  // `08-fe-handoff.md` §3/§4 for the spec, and these three endpoints appear
  // nowhere in that file (checked 2026-09-03). §9 itself is the only
  // description of them.
  LOCKED_SECTIONS: "/coach/execution-blueprint/{uniqueId}/locked/sections",
  LOCKED_CONNECTORS: "/coach/execution-blueprint/{uniqueId}/locked/connectors",
  LOCKED_SHORTLIST: "/coach/execution-blueprint/{uniqueId}/locked/shortlist",

  // ⚠️ ONE PDF FOR THE WHOLE BLUEPRINT, generated SERVER-SIDE (2026-09-03).
  // Confirmed by the user as
  //   /coach/execution-blueprint/:uniqueId/download
  // and it replaces the client-side export entirely — `_shared/lockedBlueprintPdf.js`
  // (html2canvas + jsPDF, ~500 lines and three rounds of layout fixes) is
  // deleted. It covers all three tabs in one document, so the locked view has
  // one download button rather than one per tab.
  LOCKED_DOWNLOAD: "/coach/execution-blueprint/{uniqueId}/download",
};

// Paths confirmed; response SHAPES are not — no doc describes them and nothing
// has been run against a live server yet. `normalizeLockedBlueprint` in
// `ExecutionBlueprintStore.js` is deliberately the single place those shapes
// are read, and it tolerates flat-or-wrapped bodies and several plausible
// envelopes, so a wrong guess renders an empty tab rather than throwing.
//
// This flag survives as the kill switch: set it false to stop all three calls
// at the source without unpicking the screens.
export const LOCKED_ENDPOINTS_READY = true;

export const REVIEW_STATUS = {
  ACCEPTED: "accepted",
  TO_DISCUSS: "to_discuss",
};

// item status is CEO-authoring status (EMPTY..FLAGGED) - distinct from the
// coach's own review status above. Listed here for reference; the dashboard
// doesn't render it, Review Mode will.
export const ITEM_STATUS = {
  EMPTY: "EMPTY",
  IN_PROGRESS: "IN_PROGRESS",
  AWAITING_CONFIRMATION: "AWAITING_CONFIRMATION",
  CONFIRMED: "CONFIRMED",
  REOPENED: "REOPENED",
  FLAGGED: "FLAGGED",
};

export const COMMENT_SCOPE = {
  ITEM: "item",
  SECTION: "section",
  OVERALL: "overall",
};

// The blueprint's own lifecycle state, as it appears on review/live-review
// payloads (08-fe-handoff.md §1 enumerates all ten). Only the two the coach
// surface actually branches on are named here, rather than copying an enum this
// repo can't act on most of.
//
// ⚠️ Do not confuse `blueprint_state` with the dashboard's `stage`. They are two
// different vocabularies for overlapping facts: the dashboard sends
// `stage: "LIVE_REVIEW_STARTED"` where the blueprint payload says
// `blueprint_state: "LIVE_REVIEW_IN_PROGRESS"`. `ExecutionBlueprintStore` owns
// the `stage` strings; this owns the `blueprint_state` ones. Neither is
// derivable from the other by string munging, so both stay explicit.
export const BLUEPRINT_STATE = {
  COACH_REVIEW_SUBMITTED: "COACH_REVIEW_SUBMITTED",
  LIVE_REVIEW_IN_PROGRESS: "LIVE_REVIEW_IN_PROGRESS",
};

// Tri-state, because "has the live review started?" has three honest answers and
// collapsing it to a boolean is what makes the gate misbehave.
//
// The screen must be able to tell NOT_STARTED (bounce back to the review, which
// is the gate working) from UNKNOWN (nothing has loaded yet — wait). The old
// localStorage gate never needed this: storage answers synchronously on the
// first render. A server-backed gate cannot, so a boolean would read as
// "not started" during the load and redirect the coach off a screen they are
// entitled to be on.
export const LIVE_REVIEW_GATE = {
  UNKNOWN: "unknown",
  STARTED: "started",
  NOT_STARTED: "not-started",
};
